class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.setAttribute('open', '');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.removeAttribute('open');
    if (this.activeElement) removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const next = cartDrawerNote.nextElementSibling;
    if (next && next.getAttribute('id')) cartDrawerNote.setAttribute('aria-controls', next.id);

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details')?.hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Updates ONLY the cart count bubble area (does not touch the cart icon).
   * Requires: <span id="cart-count-container"> ... </span> in header.
   */
  updateCartBubble(itemCount) {
    const container = document.querySelector('#cart-count-container');
    if (!container) return;

    const count = Number(itemCount) || 0;

    if (count <= 0) {
      container.innerHTML = '';
      return;
    }

    // Keep existing translated text if present
    const existingHidden = container.querySelector('.visually-hidden')?.textContent?.trim();
    const fallbackHidden = `Cart contains ${count} item${count === 1 ? '' : 's'}`;
    const hiddenText = existingHidden || fallbackHidden;

    container.innerHTML = `
      <div class="cart-count-bubble">
        ${count < 100 ? `<span aria-hidden="true">${count}</span>` : ``}
        <span class="visually-hidden">${this.escapeHtml(hiddenText)}</span>
      </div>
    `;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  renderContents(parsedState) {
    const count = Number(parsedState?.item_count) || 0;

    // ✅ 1) Toggle empty state on the root <cart-drawer>
    this.classList.toggle('is-empty', count === 0);

    // ✅ 2) Toggle empty state on <cart-drawer-items> too
    const itemsEl = this.querySelector('cart-drawer-items');
    if (itemsEl) itemsEl.classList.toggle('is-empty', count === 0);

    // (optional) your existing inner cleanup
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState?.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState?.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionContent = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (section.selector === '#CartDrawer') {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = sectionContent;

        const oldBar = document.getElementById('cart-menu-shipping');
        const newBar = wrapper.querySelector('#cart-menu-shipping');
        if (newBar) window.SpeciesShippingBar?.prime(newBar, oldBar);

        sectionElement.innerHTML = wrapper.innerHTML;

        const insertedBar = document.getElementById('cart-menu-shipping');
        if (insertedBar) window.SpeciesShippingBar?.activate(insertedBar);
      } else {
        sectionElement.innerHTML = sectionContent;
      }
    });

    // ✅ Keep your bubble updater
    this.updateCartBubble(parsedState?.item_count);
    window.SpeciesBoldUpsell?.emitCartLoaded?.();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });

  }


  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML || '';
  }

  // Only update cart drawer, NOT cart icon bubble
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  // ✅ THIS is the missing piece: CartItems updates run through CartDrawerItems.
  // We call the drawer's bubble updater after the standard render.
  renderContents(parsedState) {
    // Call the parent render (this updates the drawer HTML)
    super.renderContents(parsedState);

    // Then update only the bubble
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.updateCartBubble === 'function') {
      drawer.updateCartBubble(parsedState?.item_count);
    }
    window.SpeciesBoldUpsell?.emitCartLoaded?.();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
